import {
  AnalyticsResourceV2Type,
  AnalyzeExperienceFrontendTinybirdRequestBodyType,
} from "@intelligems/ig-types";
import {
  TinybirdExperimentAnalysisInput,
  TransformedDataset,
} from "@src/api/analytics/types";
import { getMaxZScore } from "@src/api/analytics/utils";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetAllCustomEvents } from "@src/api/custom-events/get-all-custom-events";
import { useSelector } from "@src/store";

export const useBuildTinybirdPayload = (): {
  build: (
    params: TinybirdExperimentAnalysisInput,
    datasetGroup: TransformedDataset,
    resourceType: AnalyticsResourceV2Type
  ) => AnalyzeExperienceFrontendTinybirdRequestBodyType;
} => {
  const maxZScore = useConfiguration<number>("max_z_score");
  const dashboardFilters = useSelector((state) => state.dashboardFilters);
  const customEvents = useGetAllCustomEvents();

  const filteredEventDtos =
    customEvents.data?.customEvents.filter((event) =>
      dashboardFilters.customEvents.includes(event.identifier)
    ) ?? [];

  const {
    compareGroupA,
    compareGroupB,
    pageName,
    experienceId,
    startTime,
    statSigView,
    endTime,
    hiddenEndTime,
    ...restOfDashboardFilters
  } = dashboardFilters;

  return {
    build: (
      {
        experience,
        audience,
        interval,
        topN,
        histogramValues,
        keyCustomEvents,
      }: TinybirdExperimentAnalysisInput,
      datasetGroup: TransformedDataset,
      resourceType: AnalyticsResourceV2Type
    ) => {
      return {
        ...restOfDashboardFilters,
        baselineVariationId: compareGroupA,
        experienceId: experience.id!,
        endTs: dashboardFilters.endTime || hiddenEndTime,
        startTs: startTime,
        datasetId: datasetGroup.datasetId,
        landingPage: dashboardFilters.landingPage || null,
        sourceSitesOrChannels: dashboardFilters.channels,
        experimentProductsOnly:
          dashboardFilters.testOrdersOnly ??
          (experience?.experienceAnalysis?.analyticsViewType ===
            "test_orders_only" ||
            experience?.experienceAnalysis?.analyticsViewType === "none"),
        maxZScore: getMaxZScore(
          dashboardFilters.maxZScore,
          dashboardFilters.isZScoreEnabled,
          maxZScore
        ),
        userBehavior: {
          addedToCart:
            dashboardFilters.reachedCheckoutStage === "added_to_cart" || null,
          viewedCheckout: null,
          viewedProductPage: dashboardFilters.userBehavior.viewedProductPage,
          viewedCollectionPage:
            dashboardFilters.userBehavior.viewedCollectionPage,
        },
        metrics: datasetGroup.metrics,
        audience: audience,
        interval: interval,
        topN: topN ? topN : null,
        kind: histogramValues?.kind,
        histogramMetric: histogramValues?.histogramMetric,
        triggeredCustomEvents: dashboardFilters.customEvents,
        customEvents: [...(keyCustomEvents || []), ...filteredEventDtos],
        resourceType,
        reachedCheckoutStage:
          dashboardFilters.reachedCheckoutStage === null ||
          dashboardFilters.reachedCheckoutStage === "any" ||
          dashboardFilters.reachedCheckoutStage === "added_to_cart"
            ? null
            : dashboardFilters.reachedCheckoutStage,
      };
    },
  } as const;
};
